import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { Button, Dialog } from "@mui/material";
import { StyledDialogFooter, StyledDialogTitle, StyledLargeDialogContent } from "../../../../components/Dialog.styled";
import CloseModalButton from "../../../../components/CloseModalButton";
import logo from "../../../../images/healthfirst_color.png";
import "./ManifestModal.css";
import constants from "../../../../constants";
import { AmalgamWasteItem, MedicalWasteItem, PharmaceuticalWasteItem, WasteManagementProcessor } from "../types";
import { standardDateFormat } from "../../../../helpers/date";
import { CustomerState } from "../../../../redux/customer";
import { ChildCustomerState } from "../../../../redux/childCustomer";

type Category = 'medicalWaste' | 'pharmaceuticalWaste' | 'amalgamWaste';

type Props = {
  open: boolean,
  handleClose: () => void,
  category: Category,
  customer: CustomerState | ChildCustomerState
  item: MedicalWasteItem | AmalgamWasteItem | PharmaceuticalWasteItem,
  processor: WasteManagementProcessor,
}

type FormRowProps = {
  label: string | undefined | null;
  value: string | number | undefined | null;
};

const FormRow: React.FC<FormRowProps> = ({ label, value }) => (
  <div className="form-row">
    <div className="underlined">{value}</div>
    <div className="bold">{label}</div>
  </div>
);

const getProcessedDate = ({ processed_date, destroyed_date }: { processed_date?: string; destroyed_date?: string; }) => {
  if (processed_date) {
    return standardDateFormat(processed_date);
  } else if (destroyed_date) {
    return standardDateFormat(destroyed_date);
  }
  return '';
};

const getProcessedWeight = ({ processed_weight, destroyed_weight }: { processed_weight?: number; destroyed_weight?: number; }) => {
  if (processed_weight) {
    return `${processed_weight.toFixed(2)} lbs`;
  } else if (destroyed_weight) {
    return `${destroyed_weight.toFixed(2)} lbs`;
  }
  return '';
}

const categoryContent: Record<Category, {
  title: string;
  mainContent: JSX.Element;
  footerContent: JSX.Element;
}> = {
  medicalWaste: {
    title: 'SHARPS/MEDICAL WASTE',
    mainContent: (<div className="sharps-details">This document is to certify that the contents of your sharps waste/regulated medical waste that had been shipped to our permitted medical waste treatment facility has been recovered, treated, and processed in accordance with all local, state, and Federal regulations. This document is used to satisfy the requirements of state and federal regulations regarding the treatment of medical waste.</div>),
    footerContent: (<div className="f-s-13">This document serves as a digital representation of the physical manifest received and signed by the processing facility. Please contact OnTraq Customer Support at <a href={`mailto:${constants.HEALTHFIRST_EMAIL}`} className="text-info">{constants.HEALTHFIRST_EMAIL}</a> if a copy of the physical manifest is required.</div>)

  },
  pharmaceuticalWaste: {
    title: "PHARMACEUTICAL WASTE",
    mainContent: (<div className="pharma-details">This document is to certify that the following customer has properly shipped the listed waste below to our permitted processing facility, and that the contents of these containers have been recovered, treated, and processed in accordance with all local, state, and federal regulations.</div>),
    footerContent: (<div className="f-s-13">Please retain a copy of this document in your office per your local and state reporting requirements. You can contact a HealthFirst Customer Support Representative at 1-800-331-1984 if you have any questions regarding the use of any of our pharmaceutical mailback products and services.</div>)
  },
  amalgamWaste: {
    title: "AMALGAM WASTE",
    mainContent: (
      <div className="amalgam-details">
        <div className="m-b-16">This document is to inform you of receipt of your dental amalgam waste at our permitted solid waste management facility in Lynnwood, Washington. This document is used to satisfy the requirements of state and federal regulations. The waste accepted by Pristine Environmental is destined for reclamation and as such is excluded from the definition of solid waste in compliance with 40 CFR 261.2(c)(3) and Washington Administrative Code WAC 173-303-016.</div>
        <div>Please keep this on file indefinitely. It serves to ensure delivery of this material to an off-site facility for reclamation and recovery of the mercury content within this waste. Pristine Environmental is permitted under the authority of the Snohomish Health District, Snohomish County, WA as a Moderate Risk Waste facility. This receipt should be acceptable for management of material designated as exempt, conditionally exempt small quantity generator waste, and universal waste.</div>
      </div>
    ),
    footerContent: (<div className="f-s-13">Please remember to maintain your vacuum lines with proper cleaning routine and avoid use of bleach or vacuum line cleaners containing bleach, oxidizing compounds or chlorinated detergents. Please contact HealthFirst Customer Support at 1-800-331-1984 if you have any questions regarding the proper maintenance of your vacuum lines.</div>)
  }
}

const ManifestModal: React.FC<Props> = ({ open, handleClose, category, customer, item, processor }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef });
  const content = categoryContent[category]

  return (
    <>
      <Dialog maxWidth="md" fullWidth={true} open={open} onClose={handleClose}>
        <CloseModalButton handleClose={handleClose} />
        <StyledDialogTitle>Manifest</StyledDialogTitle>
        <StyledLargeDialogContent ref={contentRef}>
          <div className="flex flex-space-between">
            <img style={{ height: '50px' }} src={logo} alt="company logo" />
            <span className="f-s-23">{content.title}</span>
          </div>
          <div className="processing-label">Certificate of Processing</div>
          <>{content.mainContent}</>
          <div className="form-section">
            <div className="manifest-column m-r-24">
              <FormRow label="Waste Generator" value={customer.name} />
              <FormRow label="Street Address" value={customer.street}></FormRow>
              <FormRow label="City, State, Zip" value={`${customer.city}, ${customer.state} ${customer.zip}`}></FormRow>
            </div>
            <div className="manifest-column">
              <FormRow label="Product Received" value={item?.item?.name ?? 'Unknown'} />
              <FormRow label="Product Serial Number" value={item.serial_number ?? 'N/A'} />
              <FormRow label="Item Number" value={item.item_number} />
              <FormRow label="Processed Weight" value={getProcessedWeight(item)} />
              <FormRow label="Date Processed" value={getProcessedDate(item)} />
            </div>
          </div>
          <hr />
          <div className="form-section">
            <div className="manifest-column m-r-24 bold">
              <div className="form-row">PROCESSING FACILITY</div>
              <div className="form-row">
                <div>{processor.name}</div>
                <div>{processor.street}</div>
                <div>{processor.city}, {processor.state} {processor.zip}</div>
              </div>
              <div className="f-s-14">Facility Permit: {processor.facilityPermit}</div>
            </div>
            {processor.processingRep &&
              <div className="manifest-column">
                <div className="form-row bold">PROCESSING FACILITY REPRESENTATIVE</div>
                <div className="flex form-row">
                  <span className="bold m-r-8">Print Name:</span>
                  <div className="underlined manifest-column">{processor.processingRep}</div>
                </div>
                <div className="flex flex-align-center form-row">
                  <span className="bold m-r-16">Signature:</span>
                  <div className="underlined manifest-column"><img className="signature" src={processor.signatureImage} alt="signature" /></div>
                </div>
              </div>
            }
          </div>
          <>{content.footerContent}</>
          <hr />
          <div className="f-s-13 bold">9665 v.003 08/2018</div>
        </StyledLargeDialogContent>
        <StyledDialogFooter>
          <Button variant="outlined" onClick={handleClose}>Close</Button>
          <Button onClick={reactToPrintFn} variant="contained">Print/Download</Button>
        </StyledDialogFooter>
      </Dialog>
    </>
  )
}

export default ManifestModal;